import React, { useState, useEffect, useCallback, useMemo } from 'react'
import "./PaymentMethod.css"
import { Row, Col, Form } from 'react-bootstrap'
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import tabby from "../../assets/images/payment/tabby.svg"
import tamara from "../../assets/images/payment/tamara.svg"
import moyasar from "../../assets/images/payment/moyasar.svg"
import { axiosGet } from '../../utils/apis/api';
import Cookies from "js-cookie";
import { SpinnerLoading } from "../index"
import { PAYMENT_METHODS } from "../../utils/payments/paymentMethods";

const PAGE_LIMIT = 50; 
const SEARCH_DEBOUNCE_MS = 400;
const looksLikeEmail = (value) =>
    typeof value === "string" && /\S+@\S+\.\S+/.test(value.trim());

const PaymentMethod = ({
    billingInfo,
    setBillingInfo,
    quotationId,
    paymentMethod = PAYMENT_METHODS.TABBY,
    setPaymentMethod,
}) => {
    const [activeMethod, setActiveMethod] = useState(paymentMethod || PAYMENT_METHODS.TABBY)
    const [selectedCity, setSelectedCity] = useState(null)
    const [cities, setCities] = useState([])
    const [citySearchTerm, setCitySearchTerm] = useState("")
    const [isLoading, setIsLoading] = useState(false)
    const [initialLoadDone, setInitialLoadDone] = useState(false)
    
    // Create local state that syncs with props
    const [formData, setFormData] = useState({
        email: '',
        name: '',
        phone: '',
        city: null,
        address: '',
        zip: '',
    })

    const methods = useMemo(() => ([
        {
            name: PAYMENT_METHODS.TABBY,
            image: tabby
        },
        {
            name: PAYMENT_METHODS.TAMARA,
            image: tamara
        },
        {
            name: PAYMENT_METHODS.MOYASAR,
            image: moyasar
        }
    ]), [])

    const getLocationOptionKey = useCallback((location) => {
        if (!location) return "";
        if (typeof location === "string") return location.trim().toLowerCase();
        return String(
            location.location_code ||
            location.location_name ||
            location.country_name ||
            location.name ||
            location.id ||
            ""
        )
            .trim()
            .toLowerCase();
    }, []);

    const getOptionLabel = useCallback((option) => {
        if (!option) return "";
        if (typeof option === "string") return option;
        return option.location_name || option.name || "";
    }, []);

    const resolveCustomerName = useCallback((data) => {
        const candidateNames = [
            data?.full_name,
            data?.fullName,
            data?.customer_name,
            data?.customerName,
            data?.name,
        ];

        const validName = candidateNames.find(
            (value) => typeof value === "string" && value.trim() && !looksLikeEmail(value)
        );

        if (validName) {
            return validName.trim();
        }

        const cookieFullName = Cookies.get("full_name");
        if (
            typeof cookieFullName === "string" &&
            cookieFullName.trim() &&
            !looksLikeEmail(cookieFullName)
        ) {
            return cookieFullName.trim();
        }

        return "";
    }, []);

    const resolveCityOption = useCallback(
        (cityValue, availableCities = cities) => {
            if (!cityValue) return null;

            if (typeof cityValue !== "string") {
                const matchedCity = availableCities.find(
                    (item) => getLocationOptionKey(item) === getLocationOptionKey(cityValue)
                );

                return matchedCity || cityValue;
            }

            const normalizedCity = cityValue.trim().toLowerCase();
            return (
                availableCities.find((item) => {
                    const locationName = String(
                        item?.location_name || item?.name || ""
                    )
                        .trim()
                        .toLowerCase();

                    return locationName === normalizedCity;
                }) || null
            );
        },
        [cities, getLocationOptionKey]
    );

    const fetchCities = useCallback(async (search = "", start = 0, append = false) => {
        try {
            const response = await axiosGet(
                `/api/method/alltimes.www.api.locations`,
                { search, start, limit: PAGE_LIMIT, location_type: "City" }
            );

            const newItems = response.result?.locations || [];
            setCities(prev => append ? [...prev, ...newItems] : newItems);
            return newItems;
        } catch (err) {
            console.error("City fetch error:", err);
            setCities([]);
            return [];
        }
    }, []);

    const handleCityChange = useCallback((event, value) => {
        setSelectedCity(value || null);
        setFormData((prev) => {
            const updated = { ...prev, city: value || null };
            setBillingInfo(updated);
            return updated;
        });
    }, [setBillingInfo]);

    const handleMethodClick = useCallback((methodName) => {
        setActiveMethod(methodName)
        setPaymentMethod?.(methodName)
    }, [setPaymentMethod])

    // Handle input changes
    const handleInputChange = useCallback((field, value) => {
        setFormData((prev) => {
            const updated = { ...prev, [field]: value };
            setBillingInfo(updated);
            return updated;
        });
    }, [setBillingInfo]);

    // Sync with props when they change
    useEffect(() => {
        if (!billingInfo) {
            return;
        }

        const resolvedCity = resolveCityOption(billingInfo.city);

        setFormData({
            email: billingInfo.email || '',
            name: billingInfo.name || '',
            phone: billingInfo.phone || '',
            city: resolvedCity,
            address: billingInfo.address || '',
            zip: billingInfo.zip || '',
        });
        setSelectedCity(resolvedCity);
    }, [billingInfo, resolveCityOption]);

    useEffect(() => {
        if (!paymentMethod || paymentMethod === activeMethod) {
            return;
        }

        setActiveMethod(paymentMethod);
    }, [activeMethod, paymentMethod]);

    useEffect(() => {
        if (!initialLoadDone) {
            return;
        }

        const timeoutId = setTimeout(() => {
            fetchCities(citySearchTerm);
        }, SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(timeoutId);
    }, [citySearchTerm, fetchCities, initialLoadDone]);

    // Fetch initial data
    useEffect(() => {
        const fetchInitialData = async () => {
            setIsLoading(true);
            try {
                // Fetch customer info
                const apiKey = Cookies.get("api_key");
                const apiSecret = Cookies.get("api_secret");
                const headers = {
                    Authorization: `token ${apiKey}:${apiSecret}`,
                };
                
                const customerResponse = await axiosGet(
                    "/api/method/alltimes.www.api.get_customer_primary_info",
                    {},
                    headers
                );

                console.log("Customer API Response in PaymentMethod:", customerResponse);

                if (customerResponse && customerResponse.message) {
                    const data = customerResponse.message;
                    const resolvedName = resolveCustomerName(data);
                    
                    // First set basic info
                    const initialInfo = {                   
                        email: data.email || '',
                        name: resolvedName,
                        phone: data.phone || '',
                        city: null, // Will be set after cities load
                        address: data.address || '',
                        zip: data.zip || '',
                    };
                    
                    setFormData(initialInfo);
                    setBillingInfo(initialInfo);
                    
                    // Fetch locations
                    const citiesData = await fetchCities();
                    
                    // Find matching city
                    if (data.city && data.city.trim() !== "") {
                        const cityMatch = citiesData.find(
                            (cityItem) =>
                                String(cityItem?.location_name || "").trim().toLowerCase() ===
                                data.city.trim().toLowerCase()
                        ) || null;
                        
                        if (cityMatch) {
                            setSelectedCity(cityMatch);
                            const infoWithCity = { ...initialInfo, city: cityMatch };
                            setFormData(infoWithCity);
                            setBillingInfo(infoWithCity);
                        }
                    }
                }
            } catch (error) {
                console.error("Failed to fetch initial data in PaymentMethod:", error);
            } finally {
                setIsLoading(false);
                setInitialLoadDone(true);
            }
        };
        
        if (!initialLoadDone) {
            fetchInitialData();
        }
    }, [fetchCities, initialLoadDone, resolveCustomerName, setBillingInfo]);

    if (isLoading) {
        return <div className='payment-form-loading'><SpinnerLoading /></div>;
           
    }

    return (
        <div className='payment-method-container' data-testid="payment-method-form">
            <div className='payment-method-title'>
                Choose a Payment method
            </div>
            <div className='payment-method-list'>
                {
                    methods.map((item, index) => (
                        <div  
                            className={`payment-method ${activeMethod === item.name ? 'active-method' : ''}`}
                            key={index}
                            onClick={() => handleMethodClick(item.name)}
                        >
                            <div className='payment-method-image'>
                                {item.image ? (
                                    <img src={item.image} alt={item.name} />
                                ) : (
                                    <span className='payment-method-image-fallback'>
                                        {item.name}
                                    </span>
                                )}
                            </div>
                            <div className='payment-method-name'>
                                {item.name}
                            </div>
                        </div>
                    ))
                }
            </div>
            <div className='payment-method-title'>
                Billing Info
            </div>
            <div className='payment-method-inputs'>
                <Row className='passenger-form-row'>
                    <Col xl={4} md={6}>
                        <div className='hotel-form-input-container'>
                            <label className="field-label">
                                Email
                            </label>
                            <Form.Control
                                className='hotel-form-input'
                                type='email'
                                placeholder="Enter your email"
                                value={formData.email || ''}
                                onChange={e => handleInputChange('email', e.target.value)}
                                data-testid="billing-email"
                            />
                        </div>
                    </Col>
                    <Col xl={4} md={6}>
                        <div className='hotel-form-input-container'>
                            <label className="field-label">
                                Name
                            </label>
                            <Form.Control
                                className='hotel-form-input'
                                type='text'
                                placeholder="Enter your full name"
                                value={formData.name || ''}
                                onChange={e => handleInputChange('name', e.target.value)}
                                data-testid="billing-name"
                            />
                        </div>
                    </Col>
                    <Col xl={4} md={6}>
                        <div className='hotel-form-input-container'>
                            <label className="field-label">
                                Phone
                            </label>
                            <Form.Control
                                className='hotel-form-input'
                                type='tel'
                                placeholder="Enter your phone number"
                                value={formData.phone || ''}
                                onChange={e => handleInputChange('phone', e.target.value)}
                                data-testid="billing-phone"
                            />
                        </div>
                    </Col>
                    <Col xl={4} md={6}>
                        <div className='hotel-form-input-container'>
                            <label className="field-label">
                                City
                            </label>
                            <Autocomplete
                                disablePortal
                                options={cities}
                                getOptionLabel={getOptionLabel}
                                value={selectedCity}
                                onChange={handleCityChange}
                                onInputChange={(e, newInputValue, reason) => {
                                    if (reason !== "reset") {
                                        setCitySearchTerm(newInputValue);
                                    }
                                }}
                                onFocus={() => fetchCities()}
                                isOptionEqualToValue={(option, value) =>
                                    getLocationOptionKey(option) === getLocationOptionKey(value)
                                }
                                noOptionsText="No cities found"
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        size="small"
                                        placeholder="Select city"
                                        InputProps={{
                                            ...params.InputProps,
                                            style: { direction: "ltr" },
                                            sx: {
                                                backgroundColor: "#FFF",
                                                borderRadius: "6px",
                                                padding: "9px 15px !important",
                                                fontFamily: '"Dmsans400"',
                                                fontSize: "14px !important",
                                                fontWeight: 400,
                                                letterSpacing: "0.25px",
                                            },
                                        }}
                                    />
                                )}
                                sx={{
                                    ".MuiAutocomplete-inputRoot": {
                                        fontSize: "12px",
                                    },
                                    ".MuiAutocomplete-endAdornment": {
                                        right: "9px !important",
                                    },
                                    ".MuiInputBase-input": {
                                        textAlign: "left",
                                    },
                                }}
                            />
                        </div>
                    </Col>
                    <Col xl={4} md={6}>
                        <div className='hotel-form-input-container'>
                            <label className="field-label">
                                Address
                            </label>
                            <Form.Control
                                className='hotel-form-input'
                                type='text'
                                placeholder="Enter your address"
                                value={formData.address || ''}
                                onChange={e => handleInputChange('address', e.target.value)}
                                data-testid="billing-address"
                            />
                        </div>
                    </Col>
                    <Col xl={4} md={6}>
                        <div className='hotel-form-input-container'>
                            <label className="field-label">
                                ZIP Code
                            </label>
                            <Form.Control
                                className='hotel-form-input'
                                type='text'
                                placeholder="Enter ZIP code"
                                value={formData.zip || ''}
                                onChange={e => {
                                    const value = e.target.value;
                                    handleInputChange('zip', value);
                                }}
                                data-testid="billing-zip"
                            />
                        </div>
                    </Col>
                </Row>
            </div>
        </div>
    )
}

export default PaymentMethod
